import { forwardRef, Inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EventDetailsPageDialogComponent } from '../dashboard-area/event-details/event-details-page-dialog.component';
import { WorkSessionDetailsPageDialogComponent } from '../dashboard-area/work-session-details/work-session-details-page-dialog.component';
import { ActionState, ActionStatus, Alert, Customer, Location, Partner, ServiceLevel, StoreOrderItem, SubscriptionInfo, Thing, WorkSession } from '../model';
import { LocalizationPipe } from '../shared/pipe';
import { AddOnLabelPipe } from '../shared/pipe/add-on-label.pipe';
import { PlanLabelPipe } from '../shared/pipe/plan-label.pipe';
import { DataAggregationOptions, DataAggregationUtility } from '../utility/data-aggregation-utility';
import { DataUtility } from '../utility/data-utility';
import { ActionInfoDialogV2Component } from '../widget/action-info-dialog/action-info-dialog-v2.component';
import { AppService } from './app.service';
import { AuthenticationService } from './authentication.service';
import { ContextService } from './context.service';
import { DateRangeService } from './date-range.service';
import { ExportContextService } from './export-context.service';
import { ExternalApiClientService } from './external-api-client.service';
import { ExternalCatalogService } from './external-catalog.service';
import { ExternalComponentUtilsService, PeriodFieldV2Settings, SelectionFieldSettings } from './external-component-utils.service';
import { FilterConfig, FilterService } from './filter.service';
import { NavigationService } from './navigation.service';
import { OfflineNavigationService } from './offline-navigation.service';
import { QuickHistoryDialogService } from './quick-history-dialog.service';
import { StoreCartService } from './store-cart.service';
import { StyleConfigurationService } from './style-configuration.service';
import { ThingContextService } from './thing-context.service';
import { WidgetRegistrationService } from './widget-registration.service';

@Injectable()
export class ExternalAppUtilsService {

    constructor(
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => WidgetRegistrationService)) private widgetRegistrationService: WidgetRegistrationService,
        @Inject(forwardRef(() => AppService)) private appService: AppService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => QuickHistoryDialogService)) private quickHistoryDialogService: QuickHistoryDialogService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => ExportContextService)) private exportContextService: ExportContextService,
        @Inject(forwardRef(() => FilterService)) private filterService: FilterService,
        @Inject(forwardRef(() => StoreCartService)) private storeCartService: StoreCartService,
        @Inject(forwardRef(() => ExternalComponentUtilsService)) private externalComponentUtilsService: ExternalComponentUtilsService,
        @Inject(forwardRef(() => ExternalApiClientService)) private apiClientService: ExternalApiClientService,
        @Inject(forwardRef(() => ExternalCatalogService)) private catalogService: ExternalCatalogService,
        @Inject(forwardRef(() => OfflineNavigationService)) private offlineNavigationService: OfflineNavigationService,
        @Inject(forwardRef(() => PlanLabelPipe)) private planLabelPipe: PlanLabelPipe,
        @Inject(forwardRef(() => AddOnLabelPipe)) private addOnLabelPipe: AddOnLabelPipe,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => DataAggregationUtility)) private dataAggregationUtility: DataAggregationUtility,
        @Inject(forwardRef(() => StyleConfigurationService)) private styleConfigurationService: StyleConfigurationService
    ) { }

    init(): void {
        window['appUtils'] = {
            navigateTo: (url) => this.zone.run(() => this.navigationService.publicNavigate(url)),
            localize: (label) => this.localizationPipe.transform(label),
            refreshCart: () => this.refreshCarts(),
            getTenant: () => this.zone.run(() => this.authenticationService.getTenant()),
            getUser: () => this.zone.run(() => this.authenticationService.getUser()),
            isMobile: () => this.zone.run(() => this.appService.isMobile()),
            isTablet: () => this.zone.run(() => this.appService.isTablet()),
            registerWidget: (widget) => this.zone.run(() => this.widgetRegistrationService.registerWidget(widget)),
            getPeriod: (start, end) => this.zone.run(() => this.dateRangeService.getPeriod(start, end)),
            showHistoricalData: (quickHistoryData) => this.zone.run(() => this.quickHistoryDialogService.showQuickHistory(quickHistoryData)),
            getThing: () => this.zone.run(() => this.thingContextService.getCurrentThing()),
            getThingDefinition: () => this.zone.run(() => this.thingContextService.getCurrentThingDefinition()),
            getCustomer: () => this.zone.run(() => this.handleGetCustomer()),
            getLocation: () => this.zone.run(() => this.handleGetLocation()),
            getPartner: () => this.zone.run(() => this.handleGetPartner()),
            getServiceLevel: () => this.zone.run(() => this.handleGetServiceLevel()),
            getTestSession: () => this.zone.run(() => this.thingContextService.getCurrentThingTestSession()),
            getTestSessionStartDate: () => this.zone.run(() => this.handleGetTestSessionStartDate()),
            getTestSessionStopDate: () => this.zone.run(() => this.handleGetTestSessionStopDate()),
            getUserCustomer: () => this.zone.run(() => this.authenticationService.getUserCustomer()),
            getCustomerServiceLevel: () => this.zone.run(() => this.handleGetCustomerServiceLevel()),
            getWorkSession: () => this.zone.run(() => this.thingContextService.getCurrentWorkSession()),
            getTags: () => this.zone.run(() => this.contextService.getTags()),
            getCustomerTags: () => this.zone.run(() => this.contextService.getCustomerTags()),
            getLocationTags: () => this.zone.run(() => this.contextService.getLocationTags()),
            getPartnerTags: () => this.zone.run(() => this.contextService.getPartnerTags()),
            getCurrentTimestamp: () => this.zone.run(() => new Date().getTime()),
            getAlert: () => this.zone.run(() => this.thingContextService.getCurrentAlert()),
            resolveExportFileNamePlaceholders: (name: string, startDate: number, endDate: number) => this.zone.run(() => this.exportContextService.resolveExportFileNamePlaceholders(name, startDate, endDate)),
            registerFilter: (config: FilterConfig) => this.filterService.registerFilter(config),
            createFilter: (name: string) => this.filterService.createFilter(name),
            addItemToCart: (item: StoreOrderItem) => this.addItemToStoreCart(item),
            createPeriodPicker: (element: Element, config: PeriodFieldV2Settings, context: any) => this.externalComponentUtilsService.createPeriodPicker(element, config, context),
            getApiClient: () => this.apiClientService.getApiClient(),
            getCatalog: () => this.catalogService.getCatalog(),
            downsampleData: (data: any[], limit: number, timeField: string, valueField: string) => DataUtility.downsampleData(data, limit, timeField, valueField),
            createSelectionField: (element: Element, config: SelectionFieldSettings, context: any) => this.externalComponentUtilsService.createSelectionField(element, config, context),
            hasPlan: (planName: string) => this.zone.run(() => this.hasPlan(planName)),
            hasAddOn: (addOnName: string) => this.zone.run(() => this.hasAddOn(addOnName)),
            hasFeature: (...featureNames: string[]) => this.zone.run(() => this.hasFeature(...featureNames)),
            getSubscription: () => this.zone.run(() => this.getSubscription()),
            isTrialActive: () => this.zone.run(() => this.isTrialActive()),
            getTrialStartTimestamp: () => this.zone.run(() => this.getTrialStartTimestamp()),
            getTrialEndTimestamp: () => this.zone.run(() => this.getTrialEndTimestamp()),
            getTimezone: () => this.zone.run(() => this.contextService.getTimezone()),
            getLocale: () => this.zone.run(() => this.authenticationService.getLocale()),
            getAmChartLocale: () => this.zone.run(() => this.authenticationService.getAmChartLocale()),
            getDefaultPeriods: () => this.zone.run(() => this.dateRangeService.getDefaultPeriods()),
            synchronizeOfflineRequests: () => this.zone.run(() => this.offlineNavigationService.synchronizeOfflineRequests()),
            storeGeneralData: () => this.zone.run(() => this.offlineNavigationService.storeGeneralData()),
            storeThingData: (thing: Thing) => this.zone.run(() => this.offlineNavigationService.storeThingData(thing)),
            getContextPeriod: (context: any) => this.zone.run(() => this.thingContextService.getContextPeriod(context)),
            getPlanLabel: (planName: string, partner?: boolean) => this.zone.run(() => this.planLabelPipe.transform(planName, partner)),
            getAddOnLabel: (addOnNames: string | string[], partner?: boolean) => this.zone.run(() => this.addOnLabelPipe.transform(addOnNames, partner)),
            showEventDetails: (event: Alert, options: any) => this.zone.run(() => this.goToEventDetails(event, options)),
            showWorkSessionDetails: (workSession: WorkSession, options: any) => this.zone.run(() => this.goToWorkSessionDetails(workSession, options)),
            showActionDetails: (actionStatus: ActionStatus) => this.zone.run(() => this.openActionInfoDialog(actionStatus)),
            aggregateData: (data: any[], options: DataAggregationOptions) => this.zone.run(() => this.dataAggregationUtility.aggregateData(data, options)),
            getParentThing: () => this.zone.run(() => this.thingContextService.getParentThing()),
            getRootThing: () => this.zone.run(() => this.thingContextService.getRootThing()),
            getUIToolkit: () => this.zone.run(() => this.styleConfigurationService.getUIToolkit())
        }
    }

    initForPublicPages(): void {
        window['appUtils'] = {
            navigateTo: (url) => this.zone.run(() => this.navigationService.publicNavigate(url))
        };
    }

    private handleGetCustomer(): Customer {
        let user = this.authenticationService.getUser();
        return user && user.customer ? user.customer : this.contextService.getCurrentCustomer();
    }

    private handleGetLocation(): Location {
        let user = this.authenticationService.getUser();
        return user && user.location ? user.location : this.contextService.getCurrentLocation();
    }

    private handleGetPartner(): Partner {
        let user = this.authenticationService.getUser();
        let currentPartner = this.contextService.getCurrentPartner();
        return currentPartner || user.partner;
    }

    private handleGetServiceLevel(): ServiceLevel {
        let thing = this.thingContextService.getCurrentThing();
        if (thing) {
            return thing.serviceLevel;
        }
        return null;
    }

    private handleGetTestSessionStartDate(): number {
        let thingTestSession = this.thingContextService.getCurrentThingTestSession()
        return thingTestSession ? thingTestSession.startedAt : null;
    }

    private handleGetTestSessionStopDate(): number {
        let thingTestSession = this.thingContextService.getCurrentThingTestSession()
        return thingTestSession ? thingTestSession.stoppedAt : null;
    }

    private handleGetCustomerServiceLevel(): ServiceLevel {
        let customer = this.handleGetCustomer();
        return customer ? customer.serviceLevel : null;
    }

    private refreshCarts(): void {
        this.zone.run(() => {
            this.authenticationService.refreshShoppingCart();
            this.storeCartService.refreshStoreCart();
        });
    }

    private addItemToStoreCart(item: StoreOrderItem): void {
        item.quantity = (item.quantity == null || item.quantity < 1) ? 1 : item.quantity;
        this.storeCartService.addItemToCart(item).then(() => {
            this.refreshCarts();
        }).catch(e => console.error(e));
    }

    private hasPlan(planName: string): boolean {
        if (this.authenticationService.isOrganizationUser()) {
            return true;
        }
        return this.getSubscription()?.plan == planName;
    }

    private hasAddOn(addOnName: string): boolean {
        if (this.authenticationService.isOrganizationUser()) {
            return true;
        }
        return this.getSubscription()?.addOns?.includes(addOnName);
    }

    private hasFeature(...featureNames: string[]): boolean {
        if (this.authenticationService.isOrganizationUser()) {
            return true;
        }
        return featureNames.some(f => this.getSubscription()?.features?.includes(f));
    }

    private getSubscription(): SubscriptionInfo {
        if (this.thingContextService.getCurrentThing()) {
            return this.thingContextService.getSubscription();
        } else if (this.authenticationService.isPartnerUser()) {
            return this.authenticationService.getUser().partner?.subscriptionInfo;
        } else if (this.authenticationService.isCustomerUser()) {
            return this.authenticationService.getUser().customer?.subscriptionInfo;
        }
        const customerCtx = this.contextService.getCurrentCustomer();
        if (customerCtx) {
            return customerCtx.subscriptionInfo;
        }
        const partnerCtx = this.contextService.getCurrentPartner();
        if (partnerCtx) {
            return partnerCtx.subscriptionInfo;
        }
        return null;
    }

    private isTrialActive(): boolean {
        return this.getSubscription()?.trialActive;
    }

    private getTrialStartTimestamp(): number {
        return this.getSubscription()?.trialStartTimestamp;
    }

    private getTrialEndTimestamp(): number {
        return this.getSubscription()?.trialEndTimestamp;
    }

    private goToWorkSessionDetails(workSession: WorkSession, options: any): void {
        if (workSession) {
            const isHistorical = !!workSession.endTimestamp;
            const mode = options?.mode || DetailsMode.POPUP;
            if (mode == DetailsMode.DETAILS) {
                const url = isHistorical ? '/dashboard/historical_work_session_details' : '/dashboard/active_work_session_details';
                this.navigationService.navigateTo([url, workSession.id]);
            } else {
                this.openWorkSessionDetailsDialog(workSession.id, isHistorical);
            }
        }
    }

    private openWorkSessionDetailsDialog(id: string, isHistorical: boolean): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        dialogConfig.data = { id: id, isHistorical: isHistorical };
        this.dialog.open(WorkSessionDetailsPageDialogComponent, dialogConfig);
    }

    private goToEventDetails(event: Alert, options: any): void {
        if (event) {
            const isHistorical = !!event.endTimestamp;
            const mode = options?.mode || DetailsMode.POPUP;
            if (mode == DetailsMode.DETAILS) {
                this.navigationService.navigateTo(['/dashboard/event_details', event.id]);
            } else {
                this.openEventDetailsDialog(event.id, isHistorical);
            }
        }
    }

    private openEventDetailsDialog(id: string, isHistorical: boolean): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        dialogConfig.data = { id: id, isHistorical: isHistorical };
        this.dialog.open(EventDetailsPageDialogComponent, dialogConfig);
    }

    private openActionInfoDialog(actionStatus: ActionStatus): void {
        if (actionStatus) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.minWidth = '25%';
            dialogConfig.width = '600px';
            dialogConfig.autoFocus = false;
            dialogConfig.data = { actionStatus: actionStatus, isHistorical: actionStatus.state != ActionState.TODO, workOrderContext: actionStatus.workOrder };
            dialogConfig.disableClose = true;
            this.dialog.open(ActionInfoDialogV2Component, dialogConfig);
        }
    }

}

enum DetailsMode {
    POPUP = 'POPUP',
    DETAILS = 'DETAILS'
}